import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useApi } from '@/lib/useApi';
import { ApiError } from '@/lib/api';
import { Logo } from '@/components/Shell';
import { Button, Field, Input, Select, Spinner } from '@/components/ui';
import type { PublicConfig } from '@shared/types';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'KES', 'NGN', 'ZAR', 'GHS', 'UGX', 'TZS', 'INR', 'AUD', 'CAD'];

/** First-run screen. Only reachable while the deployment has no organisation. */
export default function Setup() {
  const { bootstrap } = useAuth();
  const navigate = useNavigate();
  const { data: status, loading: checking } =
    useApi<{ needsSetup: boolean; signupEnabled: boolean }>('/auth/status');
  const { data: pub } = useApi<PublicConfig>('/public/config');
  const [params] = useSearchParams();

  // Arriving from the pricing page preselects what they clicked.
  const requested = params.get('plan');
  const [form, setForm] = useState({
    org_name: '', full_name: '', email: '', password: '', currency: 'GBP',
    org_type: 'individual',
    plan: requested === 'standard' || requested === 'custom' ? requested : 'starter',
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center"><Spinner className="h-8 w-8" /></div>;
  }
  /*
   * Deliberately does NOT bounce when an organisation already exists. This
   * screen used to be first-run only — correct for a single self-hosted
   * install, and fatal for a product being sold, because it meant the second
   * customer could never sign up. Only an explicit kill switch closes it now.
   */
  if (status && status.signupEnabled === false) return <Navigate to="/login" replace />;

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (form.password.length < 10) {
      setError('Choose a password of at least 10 characters');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await bootstrap(form);
      navigate('/app');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not complete setup');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-bg mesh">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2.5 mb-8">
          <Logo />
          <span className="font-extrabold tracking-tight text-lg">PROVIA</span>
        </div>

        <h1 className="text-title">Start your free trial</h1>
        <p className="text-muted mt-2 text-sm">
          {pub?.trial_days ?? 7} days free, no card needed. This creates your
          organisation and its owner account — you can invite managers and staff
          afterwards.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          {/*
            Asked first, because it changes the label on the next field. Plenty
            of landlords are one person with two flats, and being asked for an
            "organisation name" is the first sign that software was not built
            for them.
          */}
          <Field label="Are you letting as">
            <Select value={form.org_type} onChange={set('org_type')}>
              <option value="individual">An individual</option>
              <option value="company">A company</option>
            </Select>
          </Field>

          <Field
            label={form.org_type === 'company' ? 'Company name' : 'What your tenants should call you'}
            hint={form.org_type === 'company'
              ? undefined
              : 'Your own name is fine. It appears on messages and in the tenant portal.'}
            required
          >
            <Input
              value={form.org_name}
              onChange={set('org_name')}
              placeholder={form.org_type === 'company' ? 'Harbour Estates Ltd' : 'Jane Doe'}
              required
              autoFocus
            />
          </Field>

          <Field label="Your name" required>
            <Input value={form.full_name} onChange={set('full_name')} placeholder="Jane Doe" required />
          </Field>

          <Field label="Email" required>
            <Input
              type="email"
              value={form.email}
              onChange={set('email')}
              placeholder="you@example.com"
              autoComplete="username"
              required
            />
          </Field>

          <Field label="Password" hint="At least 10 characters" required>
            <Input
              type="password"
              value={form.password}
              onChange={set('password')}
              autoComplete="new-password"
              required
            />
          </Field>

          <Field label="Currency" hint="Used for all rent and bill amounts">
            <Select value={form.currency} onChange={set('currency')}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </Field>

          {/*
            The plan is a statement of intent, not a payment. Nothing is
            charged here and no card is asked for — the trial runs whichever
            is picked, and it can be changed in Settings afterwards.
          */}
          <Field label="Plan" hint="You can change this later. Nothing is charged today.">
            <div className="space-y-2">
              {(pub?.pricing.plans ?? []).map((p) => (
                <label
                  key={p.key}
                  className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                    form.plan === p.key
                      ? 'border-brand/50 bg-brand/5'
                      : 'border-line hover:bg-raised'
                  }`}
                >
                  <input
                    type="radio"
                    name="plan"
                    value={p.key}
                    checked={form.plan === p.key}
                    onChange={set('plan')}
                    className="mt-1 shrink-0 accent-brand"
                  />
                  <span className="min-w-0 flex-1 text-sm">
                    <span className="flex flex-wrap items-baseline justify-between gap-x-2">
                      <span className="font-medium text-ink">{p.name}</span>
                      <span className="text-muted">
                        {p.price_display ? `${p.price_display}/month` : 'Priced with you'}
                      </span>
                    </span>
                    <span className="block text-muted mt-0.5">
                      {p.property_limit
                        ? `Up to ${p.property_limit} properties`
                        : 'More than 10 properties'}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </Field>

          {error && (
            <div className="flex items-start gap-2 text-sm text-danger bg-danger/10
                            border border-danger/25 rounded-xl px-3 py-2.5">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            loading={loading}
            icon={<CheckCircle2 className="h-4 w-4" />}
          >
            Create organisation
          </Button>
        </form>

        <p className="text-sm text-faint mt-8 text-center">
          Already set up? <Link to="/login" className="link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
